import axios, { AxiosInstance } from 'axios';
import {
  Account,
  AccountPerformanceReviewResponse,
  ObjectiveProfile,
  ObjectiveTemplate,
  UtilizationSummary,
  Event,
  User,
  AccountPosition,
  PositionRecommendation,
  AccountActivityStatus,
  SuggestionIntradayBatchSummary,
  PositionIntradaySnapshot,
  TierExitPolicyConfig,
} from '../types';
import {
  MacroAiAllocationOutcome,
  MacroAiAllocationResponse,
  MacroAllocationResponse,
  MacroBriefOutcome,
  MacroBriefResponse,
  MacroDerivationMode,
  MacroDerivedResponse,
  MacroHealthResponse,
  MacroObservationsResponse,
  MacroSeriesSummary,
  MacroStateEvidenceResponse,
  MacroStateHistoryResponse,
} from '../types/macroData';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: '/api/v1',
      timeout: 300000, // Increased to 5 minutes for comprehensive multi-horizon analysis
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          window.location.href = '/dashboard/login';
        }
        return Promise.reject(error);
      },
    );
  }

  // Auth endpoints
  async login(
    email: string,
    password: string,
  ): Promise<{ token: string; user: User }> {
    const response = await this.api.post('/auth/login', { email, password });
    // Map access_token to token for frontend compatibility
    return {
      token: response.data.access_token,
      user: response.data.user,
    };
  }

  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
  ): Promise<{ token: string; user: User }> {
    const response = await this.api.post('/auth/register', {
      email,
      password,
      firstName,
      lastName,
    });
    return response.data;
  }

  async joinWaitlist(
    email: string,
    firstName: string,
    lastName: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/auth/waitlist', {
      email,
      firstName,
      lastName,
    });
    return response.data;
  }

  async getProfile(): Promise<User> {
    const response = await this.api.get('/auth/profile');
    return response.data.data; // Backend returns { data: user }
  }

  async updateProfile(profileData: Partial<User>): Promise<User> {
    const response = await this.api.put('/auth/profile', profileData);
    return response.data.data; // Backend returns { data: user }
  }

  async sendPhoneVerification(phoneNumber: string): Promise<void> {
    const response = await this.api.post('/auth/phone/send-verification', {
      phoneNumber,
    });
    return response.data;
  }

  async verifyPhoneCode(code: string): Promise<{ verified: boolean }> {
    const response = await this.api.post('/auth/phone/verify', { code });
    return response.data.data;
  }

  async getPhoneVerificationStatus(): Promise<{
    phoneNumber?: string;
    phoneNumberVerified: boolean;
    canResend: boolean;
  }> {
    const response = await this.api.get('/auth/phone/verification-status');
    return response.data.data;
  }

  async sendTestEmail(email: string): Promise<void> {
    const response = await this.api.post('/auth/test-email', { email });
    return response.data;
  }

  async sendEmailVerification(email: string): Promise<void> {
    const response = await this.api.post('/auth/email/send-verification', {
      email,
    });
    return response.data;
  }

  async verifyEmailCode(code: string): Promise<{ verified: boolean }> {
    const response = await this.api.post('/auth/email/verify', { code });
    return response.data.data;
  }

  async getEmailVerificationStatus(): Promise<{
    email?: string;
    emailVerified: boolean;
    canResend: boolean;
  }> {
    const response = await this.api.get('/auth/email/verification-status');
    return response.data.data;
  }

  /** Align list + detail account payloads with dashboard Account shape. */
  private normalizeAccount(account: any): Account {
    const balances = account?.balances ?? {};
    return {
      ...account,
      balances: {
        ...balances,
        cashBalance: balances.cash ?? balances.cashBalance ?? 0,
        marketValue: balances.longMarketValue ?? balances.marketValue ?? 0,
        totalValue: balances.totalValue ?? 0,
        buyingPower: balances.buyingPower ?? 0,
        optionBuyingPower:
          balances.buyingPowerNonMarginableTrade ?? balances.optionBuyingPower ?? 0,
        stockBuyingPower: balances.buyingPower ?? balances.stockBuyingPower ?? 0,
      },
    };
  }

  // Account endpoints
  async getAccounts(): Promise<Account[]> {
    const response = await this.api.get('/accounts');
    const accounts = Array.isArray(response.data.accounts)
      ? response.data.accounts
      : [];
    return accounts.map((account: any) => this.normalizeAccount(account));
  }

  async getAccountActivityStatus(
    accountId: string,
  ): Promise<AccountActivityStatus[]> {
    const response = await this.api.get(
      `/accounts/${accountId}/activity-status`,
    );
    const activities = Array.isArray(response.data?.activities)
      ? response.data.activities
      : [];
    return activities;
  }

  async getAccount(id: string): Promise<Account> {
    const response = await this.api.get(`/accounts/${id}`);
    const payload = response.data;
    const raw = payload?.account ?? payload;
    return this.normalizeAccount(raw);
  }

  async getAccountPerformanceReview(
    id: string,
    options: {
      from?: string;
      to?: string;
      groupBy?: 'day' | 'week' | 'month';
      includeRows?: boolean;
      limit?: number;
    } = {},
  ): Promise<AccountPerformanceReviewResponse> {
    const params = new URLSearchParams();

    if (options.from) params.append('from', options.from);
    if (options.to) params.append('to', options.to);
    if (options.groupBy) params.append('groupBy', options.groupBy);
    if (options.includeRows !== undefined) {
      params.append('includeRows', String(options.includeRows));
    }
    if (options.limit !== undefined) params.append('limit', String(options.limit));

    const queryString = params.toString();
    const response = await this.api.get(
      `/accounts/${id}/performance-review${queryString ? `?${queryString}` : ''}`,
    );

    return response.data?.data ?? response.data;
  }

  async createAccount(accountData: Partial<Account>): Promise<Account> {
    const response = await this.api.post('/accounts', accountData);
    return response.data;
  }

  async updateAccount(
    id: string,
    accountData: Partial<Account>,
  ): Promise<Account> {
    const response = await this.api.put(`/accounts/${id}`, accountData);
    return response.data;
  }

  async deleteAccount(id: string): Promise<void> {
    await this.api.delete(`/accounts/${id}`);
  }

  async syncAccount(id: string): Promise<Account> {
    const response = await this.api.post(`/accounts/${id}/sync`);
    return response.data;
  }

  async syncAllAccounts(): Promise<any> {
    const response = await this.api.post('/accounts/sync');
    return response.data;
  }

  async getAccountSummary(): Promise<any> {
    const response = await this.api.get('/accounts/summary');
    return response.data;
  }

  // Objective Profile endpoints
  async getObjectiveProfiles(filters?: any): Promise<ObjectiveProfile[]> {
    const response = await this.api.get('/objective-profiles', {
      params: filters,
    });
    return response.data;
  }

  async getObjectiveProfile(id: string): Promise<ObjectiveProfile> {
    const response = await this.api.get(`/objective-profiles/${id}`);
    return response.data;
  }

  async createObjectiveProfile(
    profileData: Partial<ObjectiveProfile>,
  ): Promise<ObjectiveProfile> {
    const response = await this.api.post('/objective-profiles', profileData);
    return response.data;
  }

  async updateObjectiveProfile(
    id: string,
    profileData: Partial<ObjectiveProfile>,
  ): Promise<ObjectiveProfile> {
    const response = await this.api.put(
      `/objective-profiles/${id}`,
      profileData,
    );
    return response.data;
  }

  async deleteObjectiveProfile(id: string): Promise<void> {
    await this.api.delete(`/objective-profiles/${id}`);
  }

  async getTemplates(): Promise<ObjectiveTemplate[]> {
    const response = await this.api.get('/objective-profiles/templates');
    return response.data;
  }

  async getTemplate(id: string): Promise<ObjectiveTemplate> {
    const response = await this.api.get(`/objective-profiles/templates/${id}`);
    return response.data;
  }

  async createFromTemplate(
    templateId: string,
    name: string,
  ): Promise<ObjectiveProfile> {
    const response = await this.api.post('/objective-profiles/from-template', {
      templateId,
      name,
    });
    return response.data;
  }

  async getUtilizationSummary(): Promise<UtilizationSummary> {
    const response = await this.api.get('/objective-profiles/summary');
    return response.data;
  }

  // Account-Objective Mapping endpoints
  async linkAccountToObjective(
    accountId: string,
    objectiveId: string,
  ): Promise<Account> {
    const response = await this.api.post(
      `/accounts/${accountId}/link-objective`,
      {
        objectiveId,
      },
    );
    return response.data;
  }

  async unlinkAccountFromObjective(accountId: string): Promise<Account> {
    const response = await this.api.post(
      `/accounts/${accountId}/unlink-objective`,
    );
    return response.data;
  }

  async getAccountsByObjective(objectiveId: string): Promise<Account[]> {
    const response = await this.api.get(
      `/accounts/by-objective/${objectiveId}`,
    );
    return response.data;
  }

  // Risk Tiers endpoints
  async getRiskTiers(accountId: string): Promise<any[]> {
    const response = await this.api.get(`/accounts/${accountId}/risk-tiers`);
    return response.data.riskTiers || [];
  }

  async addRiskTier(accountId: string, riskTier: any): Promise<Account> {
    const response = await this.api.post(
      `/accounts/${accountId}/risk-tiers`,
      riskTier,
    );
    return response.data.account;
  }

  async putPortfolioSetup(
    accountId: string,
    setup: {
      tier1?: {
        enabled: boolean;
        allocationPct: number;
        macroWeightsSource?: 'rules' | 'ai';
        autoApprove?: boolean;
      };
      tier6?: {
        enabled: boolean;
        allocationPct: number;
        minPositionConfidence?: number;
        maxPositions?: number;
        minDaysToExpiration?: number;
        regimeMode?: 'directional' | 'range_hedge';
        chopExitPolicy?: TierExitPolicyConfig;
        directionalExitPolicy?: TierExitPolicyConfig;
      };
    },
  ): Promise<Account> {
    const response = await this.api.put(`/accounts/${accountId}/portfolio-setup`, setup);
    return response.data.account;
  }

  async updateRiskTier(
    accountId: string,
    riskTierId: string,
    updates: any,
  ): Promise<Account> {
    const response = await this.api.put(
      `/accounts/${accountId}/risk-tiers/${riskTierId}`,
      updates,
    );
    return response.data.account;
  }

  async deleteRiskTier(
    accountId: string,
    riskTierId: string,
  ): Promise<Account> {
    const response = await this.api.delete(
      `/accounts/${accountId}/risk-tiers/${riskTierId}`,
    );
    return response.data.account;
  }

  // Event endpoints
  async getEventHistory(): Promise<Event[]> {
    const response = await this.api.get('/events?limit=10');
    return response.data || [];
  }

  async getAccountEvents(accountId: string): Promise<Event[]> {
    const response = await this.api.get(`/events/account/${accountId}`);
    return response.data || [];
  }

  async queryEvents(params: any): Promise<Event[]> {
    const response = await this.api.get('/events/query', { params });
    return response.data.data || [];
  }

  async getEventStatistics(): Promise<any> {
    const response = await this.api.get('/events/statistics');
    return response.data;
  }

  // Schwab endpoints
  async authorizeSchwab(): Promise<{ authUrl: string }> {
    // Get the current user's ID from the JWT token or profile
    const user = await this.getProfile();
    const response = await this.api.post('/schwab/auth/authorize', {
      userId: user.id,
    });
    return response.data;
  }

  async getSchwabAuthStatus(): Promise<{
    authenticated: boolean;
    tokenExpired: boolean;
    expiresAt?: string;
  }> {
    const response = await this.api.get('/schwab/auth/status');
    return response.data;
  }

  async revokeSchwabAuth(): Promise<void> {
    await this.api.delete('/schwab/auth/revoke');
  }

  async getSchwabAccounts(): Promise<any[]> {
    const response = await this.api.get('/schwab/accounts');
    return response.data;
  }

  // Schwab Quote endpoints
  async getSchwabQuotes(symbols: string[]): Promise<{ [symbol: string]: any }> {
    const response = await this.api.post('/schwab/quotes', { symbols });
    return response.data.quotes;
  }

  async getSchwabQuote(symbol: string): Promise<any> {
    const response = await this.api.get(`/schwab/quotes/${symbol}`);
    return response.data.quote;
  }

  async getSchwabOptionChain(symbol: string, params?: any): Promise<any> {
    const response = await this.api.get(`/schwab/options/${symbol}/chain`, {
      params,
    });
    return response.data.optionChain;
  }

  async getSchwabOptionExpirationChain(symbol: string): Promise<any> {
    const response = await this.api.get(`/schwab/expirationchain/${symbol}`);
    return response.data.optionExpirationChain;
  }

  async getSchwabConfigStatus(): Promise<any> {
    const response = await this.api.get('/schwab/status/config');
    return response.data;
  }

  async getSchwabRateLimitStatus(): Promise<any> {
    const response = await this.api.get('/schwab/status/rate-limits');
    return response.data;
  }

  // Ticker endpoints
  async getTickers(filters?: any): Promise<any> {
    const response = await this.api.get('/tickers', { params: filters });
    return response.data;
  }

  async getTickerById(id: string): Promise<any> {
    const response = await this.api.get(`/tickers/${id}`);
    return response.data;
  }

  async getTickerBySymbol(symbol: string): Promise<any> {
    const response = await this.api.get(`/tickers/symbol/${symbol}`);
    return response.data;
  }

  // AI Logs endpoints
  async getAICallLogs(filters?: any): Promise<any> {
    const response = await this.api.get('/ai/logs', { params: filters });
    return response.data;
  }

  async getAICallStats(filters?: any): Promise<any> {
    const response = await this.api.get('/ai/logs/stats', { params: filters });
    return response.data;
  }

  async getAICallLogsByCorrelationId(correlationId: string): Promise<any> {
    const response = await this.api.get(`/ai/logs/${correlationId}`);
    return response.data;
  }

  // Macro (research only) endpoints
  async getMacroSeries(): Promise<MacroSeriesSummary[]> {
    const response = await this.api.get('/macro/series');
    return response.data;
  }

  async getMacroObservations(
    seriesKey: string,
    params?: { from?: string; to?: string; includeVintages?: boolean },
  ): Promise<MacroObservationsResponse> {
    const response = await this.api.get(
      `/macro/series/${encodeURIComponent(seriesKey)}/observations`,
      { params },
    );
    return response.data;
  }

  async getMacroDerived(
    seriesKey: string,
    params: { indicator?: string; mode?: MacroDerivationMode; from?: string; to?: string },
  ): Promise<MacroDerivedResponse> {
    const response = await this.api.get(`/macro/derived/${encodeURIComponent(seriesKey)}`, {
      params,
    });
    return response.data;
  }

  async getMacroStateHistory(params: {
    mode: MacroDerivationMode;
    from?: string;
    to?: string;
  }): Promise<MacroStateHistoryResponse> {
    const response = await this.api.get('/macro/state/history', { params });
    return response.data;
  }

  async getMacroStateEvidence(params: {
    mode: MacroDerivationMode;
    asOf?: string;
  }): Promise<MacroStateEvidenceResponse> {
    const response = await this.api.get('/macro/state/evidence', { params });
    return response.data;
  }

  async getMacroAllocation(asOf?: string): Promise<MacroAllocationResponse> {
    const response = await this.api.get('/macro/allocation', { params: asOf ? { asOf } : {} });
    return response.data;
  }

  async getMacroAiAllocation(asOf?: string): Promise<MacroAiAllocationResponse> {
    const response = await this.api.get('/macro/allocation/ai', { params: asOf ? { asOf } : {} });
    return response.data;
  }

  async generateMacroAiAllocation(asOf?: string): Promise<MacroAiAllocationOutcome> {
    const response = await this.api.post('/macro/allocation/ai', asOf ? { asOf } : {});
    return response.data;
  }

  async getMacroBrief(params: { mode: MacroDerivationMode; asOf?: string }): Promise<MacroBriefResponse> {
    const response = await this.api.get('/macro/briefs', { params });
    return response.data;
  }

  async generateMacroBrief(params: { mode: MacroDerivationMode; asOf?: string }): Promise<MacroBriefOutcome> {
    const response = await this.api.post('/macro/briefs', params);
    return response.data;
  }

  async getMacroHealth(): Promise<MacroHealthResponse> {
    const response = await this.api.get('/macro/health');
    return response.data;
  }

  async runMacroIngestion(mode: 'refresh' | 'backfill' = 'refresh'): Promise<any> {
    const response = await this.api.post('/macro/cron/ingest', { mode });
    return response.data;
  }

  // Notification Audit endpoints
  async getNotificationAuditLogs(filters?: any): Promise<any> {
    const response = await this.api.get('/notifications/audit-logs', {
      params: filters,
    });
    return response.data;
  }

  async getNotificationAuditStats(filters?: any): Promise<any> {
    const response = await this.api.get('/notifications/audit-logs/stats', {
      params: filters,
    });
    return response.data;
  }

  async getNotificationAuditLogById(id: string): Promise<any> {
    const response = await this.api.get(`/notifications/audit-logs/${id}`);
    return response.data;
  }

  async searchTickers(query: string, limit?: number): Promise<any> {
    const params: any = { q: query };
    if (limit) {
      params.limit = limit;
    }
    const response = await this.api.get('/tickers/search', { params });
    return response.data;
  }

  async getTickerSummary(): Promise<any> {
    const response = await this.api.get('/tickers/summary');
    return response.data;
  }

  async getTickersForAnalysis(limit?: number): Promise<any> {
    const params = limit ? { limit } : {};
    const response = await this.api.get('/tickers/analysis', { params });
    return response.data;
  }

  async getWatchlistTickers(): Promise<any> {
    const response = await this.api.get('/tickers/watchlist');
    return response.data;
  }

  async createTicker(data: any): Promise<any> {
    const response = await this.api.post('/tickers', data);
    return response.data;
  }

  async updateTicker(id: string, data: any): Promise<any> {
    const response = await this.api.put(`/tickers/${id}`, data);
    return response.data;
  }

  async deleteTicker(id: string): Promise<void> {
    await this.api.delete(`/tickers/${id}`);
  }

  async bulkCreateTickers(tickers: any[]): Promise<any> {
    const response = await this.api.post('/tickers/bulk/create', { tickers });
    return response.data;
  }

  async bulkUpdateTickers(updates: any[]): Promise<any> {
    const response = await this.api.put('/tickers/bulk/update', { updates });
    return response.data;
  }

  async bulkDeleteTickers(ids: string[]): Promise<any> {
    const response = await this.api.delete('/tickers/bulk/delete', {
      data: { ids },
    });
    return response.data;
  }

  async updateTickerPrice(symbol: string, priceData: any): Promise<any> {
    const response = await this.api.put(
      `/tickers/symbol/${symbol}/price`,
      priceData,
    );
    return response.data;
  }

  async toggleWatchlist(symbol: string): Promise<any> {
    const response = await this.api.put(`/tickers/symbol/${symbol}/watchlist`);
    return response.data;
  }

  async getTickersByAssetType(assetType: string): Promise<any> {
    const response = await this.api.get(`/tickers/asset-type/${assetType}`);
    return response.data;
  }

  async getTickersByExchange(exchange: string): Promise<any> {
    const response = await this.api.get(`/tickers/exchange/${exchange}`);
    return response.data;
  }

  async getTickersBySector(sector: string): Promise<any> {
    const response = await this.api.get(`/tickers/sector/${sector}`);
    return response.data;
  }

  async seedTickers(): Promise<any> {
    const response = await this.api.post('/tickers/seed');
    return response.data;
  }

  async clearTickers(): Promise<any> {
    const response = await this.api.delete('/tickers/seed');
    return response.data;
  }

  async getSeedingStats(): Promise<any> {
    const response = await this.api.get('/tickers/seed/stats');
    return response.data;
  }

  // Analysis endpoints
  async analyzeTicker(data: any): Promise<any> {
    const response = await this.api.post('/analysis/analyze', data);
    return response.data;
  }

  async analyzeTickerDaily(data: any): Promise<any> {
    const response = await this.api.post('/analysis/analyze/daily', data);
    return response.data;
  }

  async analyzeTickerIntraday(data: any, params?: any): Promise<any> {
    const response = await this.api.post('/analysis/analyze/intraday', data, {
      params,
    });
    return response.data;
  }

  async batchAnalysis(data: any): Promise<any> {
    const response = await this.api.post('/analysis/analyze/batch', data);
    return response.data;
  }

  async getAnalysisHealth(): Promise<any> {
    const response = await this.api.get('/analysis/health');
    return response.data;
  }

  // Comprehensive Analysis endpoints
  async getComprehensiveAnalysis(
    symbol: string,
    timeframes?: string[],
    forceRefresh: boolean = false,
  ): Promise<any> {
    const response = await this.api.post('/analysis/comprehensive', {
      symbol: symbol.toUpperCase(),
      timeframes,
      forceRefresh,
    });
    return response.data;
  }

  async getAnalysisHistory(
    symbol: string,
    limit: number = 50,
    offset: number = 0,
  ): Promise<any> {
    const response = await this.api.get(`/analysis/history/${symbol}`, {
      params: { limit, offset },
    });
    return response.data.data || [];
  }

  async batchComprehensiveAnalysis(request: any): Promise<any> {
    const response = await this.api.post('/analysis/batch', request);
    return response.data;
  }

  async getMarketDataCoverage(symbol: string): Promise<any> {
    const response = await this.api.get(`/analysis/coverage/${symbol}`);
    return response.data;
  }

  // Health check
  async getHealth(): Promise<any> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // ===== NEW HORIZON-SPECIFIC METHODS =====

  async analyzeHorizon(
    symbol: string,
    horizon: '1hr' | '1d' | '1w' | '1m' | '1yr',
    forceRefresh: boolean = false,
  ): Promise<any> {
    const response = await this.api.post(`/analysis/horizon/${symbol}`, {
      symbol,
      horizon,
      forceRefresh,
    });
    return response.data;
  }

  async analyzeAllHorizons(
    symbol: string,
    forceRefresh: boolean = false,
  ): Promise<any> {
    const response = await this.api.post(`/analysis/horizons/${symbol}`, {
      symbol,
      forceRefresh,
    });
    return response.data;
  }

  async getMultiHorizonAnalysis(symbol: string): Promise<any> {
    const response = await this.api.get(`/analysis/horizons/${symbol}`);
    return response.data;
  }

  async getAvailableHorizons(): Promise<any> {
    const response = await this.api.get('/analysis/horizons');
    return response.data;
  }

  // ===== FEEDS ENDPOINTS =====

  async getFeeds(active?: boolean, search?: string): Promise<any> {
    const params = new URLSearchParams();
    if (active !== undefined) {
      params.append('active', active.toString());
    }
    if (search) {
      params.append('search', search);
    }
    const response = await this.api.get(`/feeds?${params.toString()}`);
    return response.data;
  }

  async getFeed(id: string): Promise<any> {
    const response = await this.api.get(`/feeds/${id}`);
    return response.data;
  }

  async createFeed(feedData: any): Promise<any> {
    const response = await this.api.post('/feeds', feedData);
    return response.data;
  }

  async updateFeed(id: string, feedData: any): Promise<any> {
    const response = await this.api.patch(`/feeds/${id}`, feedData);
    return response.data;
  }

  async deleteFeed(id: string): Promise<void> {
    await this.api.delete(`/feeds/${id}`);
  }

  async fetchFeed(id: string): Promise<any> {
    const response = await this.api.post(`/feeds/${id}/fetch`);
    return response.data;
  }

  async createBulkFeeds(urls: string[]): Promise<any> {
    const response = await this.api.post('/feeds/bulk', { urls });
    return response.data;
  }

  async validateFeedUrl(url: string): Promise<any> {
    const response = await this.api.post('/feeds/validate', { url });
    return response.data;
  }

  // Article endpoints
  async getFeedArticles(id: string, options: any = {}): Promise<any> {
    const response = await this.api.get(`/feeds/${id}/articles`, {
      params: options,
    });
    return response.data;
  }

  async getAllArticles(options: any = {}): Promise<any> {
    const response = await this.api.get('/feeds/articles/all', {
      params: options,
    });
    return response.data;
  }

  async getMatchingArticles(tickerSymbols: string[]): Promise<any> {
    const response = await this.api.get('/feeds/articles/all', {
      params: {
        tickerSymbols: tickerSymbols.join(','),
        limit: 20, // Limit to 20 most recent articles
        sortBy: 'publishedAt',
        sortOrder: 'desc',
        analyzedOnly: true, // Only show analyzed articles
      },
    });
    return response.data;
  }

  async getArticleStats(): Promise<any> {
    const response = await this.api.get('/feeds/articles/stats');
    return response.data;
  }

  async markArticleAsRead(id: string, isRead: boolean): Promise<any> {
    const response = await this.api.patch(`/feeds/articles/${id}/read`, {
      isRead,
    });
    return response.data;
  }

  async toggleArticleBookmark(id: string): Promise<any> {
    const response = await this.api.patch(`/feeds/articles/${id}/bookmark`);
    return response.data;
  }

  // Article Analysis endpoints
  async analyzeArticlesBatch(request: any): Promise<any> {
    const response = await this.api.post('/article-analysis/batch', request);
    return response.data;
  }

  async analyzeArticle(articleId: string): Promise<any> {
    const response = await this.api.post(
      `/article-analysis/${articleId}/analyze`,
    );
    return response.data;
  }

  async getArticleAnalysis(articleId: string): Promise<any> {
    const response = await this.api.get(
      `/article-analysis/${articleId}/analysis`,
    );
    return response.data;
  }

  async getTickerMentions(ticker: string, limit: number = 50): Promise<any> {
    const response = await this.api.get(
      `/article-analysis/ticker/${ticker}/mentions`,
      {
        params: { limit },
      },
    );
    return response.data;
  }

  async getMarketSentimentSummary(days: number = 7): Promise<any> {
    const response = await this.api.get('/article-analysis/market-sentiment', {
      params: { days },
    });
    return response.data;
  }

  async cleanupOldAnalyses(daysToKeep: number = 30): Promise<any> {
    const response = await this.api.post('/article-analysis/cleanup', null, {
      params: { daysToKeep },
    });
    return response.data;
  }

  // Market Thesis endpoints
  async generateMarketThesis(data: {
    timeframe: '1_day' | '1_2_weeks' | '1_month' | '6_months_plus';
    indices?: string[];
    userId?: string;
  }): Promise<any> {
    const response = await this.api.post(
      '/analysis/orchestrator/market-thesis',
      data,
    );
    return response.data;
  }

  async getLatestDirectionalTheses(
    timeframe: '1_day' | '1_2_weeks' | '1_month' | '6_months_plus',
  ): Promise<any[]> {
    const response = await this.api.get(
      `/analysis/orchestrator/market-thesis/latest/${timeframe}`,
    );
    return response.data;
  }

  async getDirectionalThesisHistory(
    timeframe: '1_day' | '1_2_weeks' | '1_month' | '6_months_plus',
    direction: 'up' | 'sideways' | 'down',
  ): Promise<any[]> {
    const response = await this.api.get(
      `/analysis/orchestrator/market-thesis/history/${timeframe}/${direction}`,
    );
    return response.data;
  }

  async getDirectionalThesisById(id: string): Promise<any> {
    const response = await this.api.get(
      `/analysis/orchestrator/market-thesis/${id}`,
    );
    return response.data;
  }

  // Performance Snapshot endpoints
  async getPerformanceSnapshotsForThesis(
    thesisId: string,
    limit?: number,
  ): Promise<any[]> {
    const params = limit ? { limit } : {};
    const response = await this.api.get(
      `/analysis/orchestrator/market-thesis/${thesisId}/performance-snapshots`,
      { params },
    );
    return response.data;
  }

  async getPerformanceSnapshotsByTimeframeAndDirection(
    timeframe: string,
    direction: 'up' | 'sideways' | 'down',
    limit?: number,
    minConfidence?: number,
    maxConfidence?: number,
  ): Promise<any[]> {
    const params: any = {};
    if (limit) params.limit = limit;
    if (minConfidence !== undefined) params.minConfidence = minConfidence;
    if (maxConfidence !== undefined) params.maxConfidence = maxConfidence;
    const response = await this.api.get(
      `/analysis/orchestrator/market-thesis/performance-snapshots/timeframe/${timeframe}/direction/${direction}`,
      { params },
    );
    return response.data;
  }

  async getLatestPerformanceSnapshot(thesisId: string): Promise<any> {
    const response = await this.api.get(
      `/analysis/orchestrator/market-thesis/${thesisId}/performance-snapshots/latest`,
    );
    return response.data;
  }

  async getThesisPerformanceStats(thesisId: string): Promise<any> {
    const response = await this.api.get(
      `/analysis/orchestrator/market-thesis/${thesisId}/performance-stats`,
    );
    return response.data;
  }

  async getTimeframeDirectionPerformanceStats(
    timeframe: string,
    direction: 'up' | 'sideways' | 'down',
  ): Promise<any> {
    const response = await this.api.get(
      `/analysis/orchestrator/market-thesis/performance-stats/timeframe/${timeframe}/direction/${direction}`,
    );
    return response.data;
  }

  async getOverallPerformanceStats(): Promise<any> {
    const response = await this.api.get(
      `/analysis/orchestrator/market-thesis/performance-stats/overall`,
    );
    return response.data;
  }

  async getPerformanceStatsByConfidence(
    minConfidence?: number,
    maxConfidence?: number,
  ): Promise<any> {
    const params: any = {};
    if (minConfidence !== undefined) params.minConfidence = minConfidence;
    if (maxConfidence !== undefined) params.maxConfidence = maxConfidence;
    const response = await this.api.get(
      `/analysis/orchestrator/market-thesis/performance-stats/by-confidence`,
      { params },
    );
    return response.data;
  }

  async getPerformanceStatsByConfidenceThreshold(
    threshold: number = 45,
    timeframe?: string,
    direction?: 'up' | 'sideways' | 'down',
  ): Promise<any> {
    const params: any = { threshold };
    if (timeframe) params.timeframe = timeframe;
    if (direction) params.direction = direction;
    const response = await this.api.get(
      `/analysis/orchestrator/market-thesis/performance-stats/by-confidence-threshold`,
      { params },
    );
    return response.data;
  }

  async getTargetAchievementStats(): Promise<any> {
    const response = await this.api.get(
      `/analysis/orchestrator/market-thesis/target-achievement-stats`,
    );
    return response.data;
  }

  async createPerformanceSnapshot(thesisId: string): Promise<any> {
    const response = await this.api.post(
      `/analysis/orchestrator/market-thesis/${thesisId}/performance-snapshot`,
    );
    return response.data;
  }

  async updateAllPerformanceSnapshots(): Promise<any> {
    const response = await this.api.post(
      `/analysis/orchestrator/market-thesis/performance-snapshots/update-all`,
    );
    return response.data;
  }

  async getTickerAnalysisSummary(
    symbol: string,
    includeArticleMentions: boolean = true,
  ): Promise<any> {
    const response = await this.api.get(
      `/analysis-v2/ticker-summary/${symbol.toUpperCase()}`,
      {
        params: {
          includeArticleMentions: includeArticleMentions.toString(),
        },
      },
    );
    return response.data;
  }

  async getTickerAnalysisSummaries(
    symbols: string[],
    includeArticleMentions: boolean = true,
    maxArticleMentions?: number,
  ): Promise<any[]> {
    const response = await this.api.get('/analysis-v2/ticker-summary', {
      params: {
        symbols: symbols.join(','),
        includeArticleMentions: includeArticleMentions.toString(),
        maxArticleMentions: maxArticleMentions?.toString(),
      },
    });
    return response.data;
  }

  async getTickerAnalysisHistory(
    symbol: string,
    analysisType: string,
    days: number = 30,
  ): Promise<any[]> {
    const response = await this.api.get(
      `/analysis-v2/ticker-summary/${symbol.toUpperCase()}/history/${analysisType}`,
      {
        params: { days: days.toString() },
      },
    );
    return response.data;
  }

  async getTickerArticleMentions(
    symbol: string,
    limit: number = 20,
  ): Promise<any> {
    const response = await this.api.get(
      `/tickers/${symbol}/article-mentions?limit=${limit}`,
    );
    return response.data;
  }

  // Account Position Management
  async getAccountPositions(accountId: string): Promise<{
    positions: AccountPosition[];
    count: number;
    timestamp: string;
  }> {
    const response = await this.api.get(
      `/account-orchestrator/${accountId}/positions`,
    );
    return response.data;
  }

  async getAccountPositionsPaginated(
    accountId: string,
    options: {
      status?: string;
      limit?: number;
      skip?: number;
    } = {},
  ): Promise<{
    positions: AccountPosition[];
    count: number;
    total: number;
    hasMore: boolean;
    timestamp: string;
  }> {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.skip) params.append('skip', options.skip.toString());

    const response = await this.api.get(
      `/account-orchestrator/accounts/${accountId}/positions?${params.toString()}`,
    );
    return response.data;
  }

  async getPositionRecommendations(
    accountId: string,
    status?: string,
  ): Promise<{
    recommendations: PositionRecommendation[];
    count: number;
    timestamp: string;
  }> {
    const params = new URLSearchParams();
    if (status) {
      params.append('status', status);
    }

    const url = `/account-orchestrator/${accountId}/recommendations${
      params.toString() ? `?${params.toString()}` : ''
    }`;

    const response = await this.api.get(url);
    return response.data;
  }

  async getActivityLogs(
    accountId: string,
    limit: number = 300,
    positionId?: string,
    activityType?: string,
  ): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());

    if (positionId) {
      params.append('positionId', positionId);
    }

    if (activityType) {
      params.append('activityType', activityType);
    }

    const response = await this.api.get(
      `/account-orchestrator/position-entry-trades/${accountId}/activity-logs?${params.toString()}`,
    );
    return response.data;
  }

  async getAccountPerformanceHistory(
    accountId: string,
    options?:
      | '1d'
      | '1w'
      | '1m'
      | '1y'
      | {
          timeframe?: '1d' | '1w' | '1m' | '1y';
          from?: string;
          to?: string;
        },
  ): Promise<{
    timeframe: '1d' | '1w' | '1m' | '1y' | 'custom';
    bucketSizeMs: number;
    maxPoints: number;
    rangeStart: string;
    rangeEnd: string;
    points: Array<{
      timestamp: string;
      totalValue: number;
      utilization?: number;
      positionCount?: number;
      totalUnrealizedPL?: number;
    }>;
  }> {
    const normalized =
      typeof options === 'string' || options === undefined
        ? { timeframe: options }
        : options;

    const params = new URLSearchParams();
    if (normalized.from && normalized.to) {
      params.append('from', normalized.from);
      params.append('to', normalized.to);
    } else if (normalized.timeframe) {
      params.append('timeframe', normalized.timeframe);
    }

    const response = await this.api.get(
      `/accounts/${accountId}/performance-history${params.toString() ? `?${params.toString()}` : ''}`,
    );
    return response.data;
  }

  async getSuggestionIntradayCheckpoints(
    accountId: string,
    options: {
      from?: string;
      to?: string;
      sessionDate?: string;
    } = {},
  ): Promise<{
    batches: SuggestionIntradayBatchSummary[];
    count: number;
    timestamp: string;
  }> {
    const params = new URLSearchParams();
    if (options.from) params.append('from', options.from);
    if (options.to) params.append('to', options.to);
    if (options.sessionDate) params.append('sessionDate', options.sessionDate);

    const queryString = params.toString();
    const response = await this.api.get(
      `/accounts/${accountId}/suggestion-intraday-checkpoints${
        queryString ? `?${queryString}` : ''
      }`,
    );
    return response.data;
  }

  async getPositionIntradaySnapshots(
    accountId: string,
    positionId: string,
    options: {
      sessionDate?: string;
      from?: string;
      to?: string;
    } = {},
  ): Promise<{
    snapshots: PositionIntradaySnapshot[];
    count: number;
    timestamp: string;
  }> {
    const params = new URLSearchParams();
    if (options.sessionDate) params.append('sessionDate', options.sessionDate);
    if (options.from) params.append('from', options.from);
    if (options.to) params.append('to', options.to);

    const queryString = params.toString();
    const response = await this.api.get(
      `/account-orchestrator/${accountId}/positions/${positionId}/intraday-snapshots${
        queryString ? `?${queryString}` : ''
      }`,
    );
    return response.data as {
      snapshots: PositionIntradaySnapshot[];
      count: number;
      timestamp: string;
    };
  }

  async updatePositionStatus(
    positionId: string,
    status: string,
    notes?: string,
  ): Promise<{
    success: boolean;
    message: string;
    position: AccountPosition;
    timestamp: string;
  }> {
    const response = await this.api.put(
      `/account-orchestrator/positions/${positionId}/status`,
      {
        status,
        notes,
      },
    );
    return response.data;
  }

  async cancelPosition(positionId: string): Promise<{
    success: boolean;
    message: string;
    cancelledOrders: string[];
    closingOrders: string[];
    timestamp: string;
  }> {
    const response = await this.api.delete(
      `/account-orchestrator/positions/${positionId}/cancel`,
    );
    return response.data;
  }

  async getHoldingsOrphans(accountId: string): Promise<{
    success: boolean;
    accountId: string;
    capturedAt: string;
    schwabPositionCount: number;
    schwabOnly: Array<{
      symbol: string;
      longQuantity: number;
      shortQuantity: number;
      marketValue: number;
      isShort: boolean;
    }>;
    marlinOnly: Array<{
      positionId: string;
      symbol: string;
      status?: string;
      lockedOcc?: string;
      marlinSignedQty: number;
    }>;
    qtyOrSide: Array<{
      positionId: string;
      symbol: string;
      lockedOcc: string;
      marlinSignedQty: number;
      schwabSignedQty: number;
    }>;
    timestamp: string;
  }> {
    const response = await this.api.get(
      `/account-orchestrator/${accountId}/holdings-orphans`,
    );
    return response.data;
  }

  async closeHoldingsOrphan(
    accountId: string,
    symbol: string,
  ): Promise<{
    success: boolean;
    message: string;
    symbol: string;
    side: 'SELL' | 'BUY';
    instruction: 'SELL_TO_CLOSE' | 'BUY_TO_CLOSE';
    quantity: number;
    schwabOrderId?: string;
    errorMessage?: string;
    timestamp: string;
  }> {
    const response = await this.api.post(
      `/account-orchestrator/${accountId}/holdings-orphans/close`,
      { symbol },
    );
    return response.data;
  }

  async orchestrateAccountPositions(accountId: string): Promise<any> {
    const response = await this.api.post(
      `/account-orchestrator/${accountId}/orchestrate`,
    );
    return response.data;
  }

  // Orchestration Audit Logs endpoints
  async getOrchestrationAuditLogs(
    accountId: string,
    options?: {
      limit?: number;
      skip?: number;
      flowType?: 'thesis-driven' | 'legacy' | 'skipped';
      status?: 'success' | 'partial_success' | 'failed' | 'skipped';
    },
  ): Promise<{
    logs: any[];
    count: number;
    total: number;
    hasMore: boolean;
  }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.skip) params.append('skip', options.skip.toString());
    if (options?.flowType) params.append('flowType', options.flowType);
    if (options?.status) params.append('status', options.status);

    const queryString = params.toString();
    const url = `/account-orchestrator/${accountId}/audit-logs${
      queryString ? `?${queryString}` : ''
    }`;

    const response = await this.api.get(url);
    return response.data;
  }

  async getOrchestrationAuditLogById(
    auditLogId: string,
  ): Promise<{ log: any }> {
    const response = await this.api.get(
      `/account-orchestrator/audit-logs/${auditLogId}`,
    );
    return response.data;
  }

  async getPositionsByIds(
    positionIds: string[],
  ): Promise<{ positions: any[]; count: number }> {
    const response = await this.api.post(
      `/account-orchestrator/positions/by-ids`,
      { positionIds },
    );
    return response.data;
  }

  async getRecommendationsByIds(
    recommendationIds: string[],
  ): Promise<{ recommendations: any[]; count: number }> {
    const response = await this.api.post(
      `/account-orchestrator/recommendations/by-ids`,
      { recommendationIds },
    );
    return response.data;
  }

  async getTierBudgets(accountId: string) {
    const response = await this.api.get(`/portfolio/accounts/${accountId}/tier-budgets`);
    return response.data as import('../types/tier1Portfolio').TierBudgetSnapshot[];
  }

  async getAccountTierContext(accountId: string) {
    const response = await this.api.get(
      `/portfolio/accounts/${accountId}/tier-context`,
    );
    return response.data as import('../types/tierContext').AccountTierContextResponse;
  }

  async getTierContextSummary() {
    const response = await this.api.get('/portfolio/tier-context/summary');
    return response.data as import('../types/tierContext').TierContextSummaryResponse;
  }

  async getTierPerformanceHistory(
    accountId: string,
    riskTier: number,
    timeframe: '1d' | '1w' | '1m' | '1y' = '1w',
  ) {
    const params = new URLSearchParams({
      riskTier: String(riskTier),
      timeframe,
    });
    const response = await this.api.get(
      `/portfolio/accounts/${accountId}/tier-performance-history?${params.toString()}`,
    );
    return response.data as import('../types/tierPerformance').TierPerformanceHistoryResponse;
  }

  async listTier1Plans(accountId: string, tierId: string) {
    const response = await this.api.get(
      `/portfolio/accounts/${accountId}/tiers/${tierId}/plans`,
    );
    return response.data;
  }

  async getTier1ActivePlan(accountId: string, tierId: string) {
    const response = await this.api.get(
      `/portfolio/accounts/${accountId}/tiers/${tierId}/plan/active`,
    );
    return response.data;
  }

  async discardPendingTier1Plans(accountId: string, tierId: string) {
    const response = await this.api.post(
      `/portfolio/accounts/${accountId}/tiers/${tierId}/plans/discard-pending`,
    );
    return response.data as { discardedCount: number };
  }

  async generateTier1Plan(
    accountId: string,
    tierId: string,
    options?: { replacePending?: boolean },
  ) {
    const response = await this.api.post(
      `/portfolio/accounts/${accountId}/tiers/${tierId}/plans/generate`,
      { replacePending: options?.replacePending ?? true },
    );
    return response.data;
  }

  async approveTier1Plan(planId: string, acknowledgeMissingThesis?: boolean) {
    const response = await this.api.post(`/portfolio/plans/${planId}/approve`, {
      acknowledgeMissingThesis,
    });
    return response.data;
  }

  async getTier1Implementation(accountId: string, tierId: string) {
    const response = await this.api.get(
      `/portfolio/accounts/${accountId}/tiers/${tierId}/implementation`,
    );
    return response.data as import('../types/tier1Portfolio').Tier1ImplementationReport;
  }

  async getTier1IpsHistory(accountId: string, tierId: string) {
    const response = await this.api.get(
      `/portfolio/accounts/${accountId}/tiers/${tierId}/ips/history`,
    );
    return response.data as import('../types/tier1Portfolio').Tier1PolicyHistoryEntry[];
  }

  async runTier1Review(accountId: string, tierId: string) {
    const response = await this.api.post(
      `/portfolio/accounts/${accountId}/tiers/${tierId}/reviews/run`,
    );
    return response.data;
  }

  async listTier1RebalanceProposals(accountId: string, tierId: string) {
    const response = await this.api.get(
      `/portfolio/accounts/${accountId}/tiers/${tierId}/rebalance-proposals`,
    );
    return response.data as import('../types/tier1Portfolio').Tier1RebalanceProposal[];
  }

  async getTier1Workbench(accountId: string, tierId: string) {
    const response = await this.api.get(
      `/portfolio/accounts/${accountId}/tiers/${tierId}/workbench`,
    );
    return response.data as import('../types/tier1Portfolio').Tier1Workbench;
  }

  async approveTier1RebalanceProposal(
    proposalId: string,
    body?: {
      decisions?: Array<{ orderIndex: number; decision: 'approved' | 'denied' }>;
    },
  ) {
    const response = await this.api.post(
      `/portfolio/rebalance-proposals/${proposalId}/approve`,
      body ?? {},
    );
    return response.data;
  }

  async rejectTier1RebalanceProposal(proposalId: string) {
    const response = await this.api.post(
      `/portfolio/rebalance-proposals/${proposalId}/reject`,
    );
    return response.data;
  }

}

export const apiService = new ApiService();
export default apiService;
